import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Box, Button, Tabs, Tab, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DisplayFile } from '../DisplayFile/DisplayFile';
import PropTypes from 'prop-types';
import { Loading } from '../../Loading/Loading';

const useStyles = makeStyles(() => ({
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  tabs: {
    borderBottom: '1px solid #e0e0e0',
    marginBottom: '16px',
    flexShrink: 0,
  },
  filesContainer: {
    display: 'grid',
    gap: '12px',
    gridTemplateColumns: 'repeat(5, auto)',
    justifyContent: 'start',
    maxHeight: '446px',
    minHeight: '0',
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingLeft: '10px',
    gridAutoRows: 'max-content',
    flex: 1,
    alignContent: 'start',
  },
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '200px',
    width: '100%',
    color: '#666',
  },
}));

export const TabbedGallery = ({
  selected,
  setSelected,
  actionCallback,
  useGallery,
  handleEdit,
}) => {
  const [originalFiles, setOriginalFiles] = useState([]);
  const [editedFiles, setEditedFiles] = useState([]);
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const { deleteFile, getFilesByType } = useGallery();

  const loadFiles = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const [originalResponse, editedResponse] = await Promise.all([
        getFilesByType('original'),
        getFilesByType('edited'),
      ]);

      setOriginalFiles(originalResponse);
      setEditedFiles(editedResponse);
      actionCallback({
        type: 'load',
        status: 'success',
        payload: {
          originalFiles: originalResponse,
          editedFiles: editedResponse,
        },
      });
    } catch (err) {
      console.error('TabbedGallery - Error loading files:', err);
      setError(true);
      actionCallback({ type: 'load', status: 'error', payload: err });
    } finally {
      setLoading(false);
    }
  }, [actionCallback, getFilesByType]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleDelete = useCallback(
    (id) => {
      (async () => {
        setDeleting(true);
        try {
          await deleteFile(id);

          // Remove from both arrays
          setOriginalFiles((prevOriginal) =>
            prevOriginal.filter((file) => file.id !== id)
          );
          setEditedFiles((prevEdited) =>
            prevEdited.filter((file) => file.id !== id)
          );
          setSelected([]);
          actionCallback({ type: 'delete', status: 'success', payload: {} });
        } catch (err) {
          actionCallback({ type: 'delete', status: 'error', payload: err });
        } finally {
          setDeleting(false);
        }
      })();
    },
    [deleteFile, actionCallback, setSelected]
  );

  const handleSelectFile = useCallback(
    (file) => {
      setSelected([file]);
    },
    [setSelected]
  );

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);

  const currentFiles = useMemo(() => {
    const files = activeTab === 0 ? originalFiles : editedFiles;
    return files;
  }, [activeTab, originalFiles, editedFiles]);

  if (deleting)
    return (
      <Box
        style={{
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Loading />
        <Typography>Deleting File</Typography>
      </Box>
    );

  if (loading) return <Loading />;

  if (error)
    return (
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <Typography>Error Loading Files.</Typography>
        <Button style={{ marginLeft: '8px' }} onClick={loadFiles}>
          Retry
        </Button>
      </Box>
    );

  return (
    <Box className={classes.container}>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        className={classes.tabs}
      >
        <Tab label={`Original Files (${originalFiles.length})`} />
        <Tab label={`Edited Files (${editedFiles.length})`} />
      </Tabs>

      <Box className={classes.filesContainer}>
        {currentFiles.map((file) => {
          return (
            <DisplayFile
              key={file.id}
              selected={selected[0]?.id === file.id}
              setSelected={() => handleSelectFile(file)}
              deleteFile={() => handleDelete(file.id)}
              editFile={() => handleEdit(file)}
              file={file}
            />
          );
        })}
        {currentFiles.length === 0 && (
          <Box className={classes.emptyState}>
            <Typography>
              No {activeTab === 0 ? 'original' : 'edited'} files found
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

TabbedGallery.propTypes = {
  selected: PropTypes.array,
  setSelected: PropTypes.func,
  actionCallback: PropTypes.func,
  useGallery: PropTypes.func,
  handleEdit: PropTypes.func,
};

export default TabbedGallery;
